import { getErrorResponse } from "./errorUtils"

interface FieldErrorPayload {
  field: string
  error: string
}

class SetNameError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = "SetNameError"
  }
}

class UserValidationError extends Error {
  readonly fields: string[]

  constructor(fields: string[], options?: ErrorOptions) {
    super("Failed to validate user fields", options)
    this.name = "UserValidationError"
    this.fields = fields
  }
}

const parseUserValidationFieldErrors = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) {
    return null
  }

  const fields: string[] = []

  for (const item of value) {
    if (
      typeof item !== "object" ||
      item === null ||
      !("field" in item) ||
      !("error" in item) ||
      typeof item.field !== "string" ||
      typeof item.error !== "string"
    ) {
      return null
    }

    const payload = item as FieldErrorPayload
    fields.push(payload.field)
  }

  return fields.length > 0 ? fields : null
}

export const normalizeUserValidationError = (error: unknown): unknown => {
  const response = getErrorResponse(error)

  if (response?.status !== 400) {
    return error
  }

  const fields = parseUserValidationFieldErrors(response.data)

  if (!fields) {
    return error
  }

  return new UserValidationError(fields, { cause: error })
}

export const normalizeSetNameError = (error: unknown): unknown => {
  const response = getErrorResponse(error)

  if (
    response?.status === 400 &&
    typeof response.data === "string" &&
    response.data.trim().length > 0
  ) {
    return new SetNameError(response.data, { cause: error })
  }

  return error
}

export const isUserValidationError = (
  error: unknown,
): error is UserValidationError => {
  return error instanceof UserValidationError
}

export const isSetNameError = (error: unknown): error is SetNameError => {
  return error instanceof SetNameError
}
